import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { of, concat, from, Subscription, Subject, catchError, forkJoin, Observable, defaultIfEmpty, toArray } from 'rxjs';
import { tap, map, last, concatMap, filter, finalize } from 'rxjs/operators';
import { Table } from 'primeng/table';
import { FilterService, MessageService } from 'primeng/api';
import { Title } from "@angular/platform-browser";

import { environment } from 'src/environments/environment';

import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { SecurityGroups, SecurityGroupMembers, KeycloakUsersList, ReportServer, ReportServerUserAccess, JsReportUser } from 'src/app/shared/securitygroups';
import { ClientApiService } from 'src/app/services/client-api.service';
import { SecureApiService } from 'src/app/services/secure-api.service';
import { HelperServerApiServiceService } from 'src/app/services/helper-server-api-service.service'; 
import { AuthGuard } from 'src/app/shared/auth.service'
import { PerListEntry, } from 'src/app/shared/per-list-entry';

/*
  * EditUserAccessTabComponent
  * 
  * This component is used to edit user access groups and security groups.
  * It allows the user to add, edit, and delete user access groups and security groups.
  * 
  * It uses the SecureApiService to communicate with the backend API.
  * It also uses the GetglobalsettingsService to get the global settings.
  * 
  * The component is encapsulated in a ViewEncapsulation.None, which means that the styles are not scoped to this component.
  *
  * Here we would discuss new feature to simplify adding new users to the system.
  *
  * In general the sequence of actions is:
  * 
  * 1) Create new user in main company with particular e-mail address
  * 2) Assign user to the existing security group into system
  * 3) Then we need to link this user to the login on AA server (Keycloak)
  *    Or we have to add this user to the Keycloak server
  *    Then we have to pull user id from keycloak and assign it to the user in the system
  * 4) after that we have to create new user on report servers
  *    Report server(s) used the same Keycloak Server to authenticate users
  * 
  * 5) We have HelperServerApiServiceService to implement all the functionality
  *
  * For HelperServerApiServiceService we have to implement the following:
  * - getUserInfo() - to get user info from Keycloak server (this is already implemented as getObservable2KeycloakUserInfo())
  * - getUserList() - to get user list from Keycloak server
  * - getUserGroups() - to get user groups from Keycloak server (not sure if this is needed)
  * - getUserRoles() - to get user roles from Keycloak server
  * - getUserPermissions() - to get user permissions from Keycloak server
  * - getUserInfoById() - to get user info by id from Keycloak server
  * - getUserInfoByLogin() - to get user info by login from Keycloak server
  *
  * - createUser() - to create new user on Keycloak server
  * - updateUser() - to update user on Keycloak server
  * - deleteUser() - to delete user on Keycloak server
  *
  * We can either create new user on Keycloak server or link existing user to the system.
  * So we need two UI/UX flows:
  * 1) Create new user flow
  * 2) Link existing user flow
  *
  * During the link existing user flow we have check whether user ID already used in the system.
  * If it is used - we have to show error message and do not allow to link user.
  *
  * We need new component like dropdown list to select user from the Keycloak server.
  * Or create new one and load it from Keycloak server.
  *
  * As potential patrameters for the new user we need to pass the Person class object
  *
  * TODO:
  * - Implement the new component to select user from Keycloak server or create new one
  * - Implement corresponding methods in HelperServerApiServiceService
  *
  * Use user access group as treshold to assign portal-admin rights (need to check exact value/group name)
  *
  * Plus assign other Keycloak groups to the user acordingly to userSecurityGroup value
  *
  * From Keycloak server point of view ther is only one user with particular login - e-mail.
  * For security reason we would use second e-mail from user, hidden from printable documents
  * TODO: Have to check the templates on report server to use only primary user e-mail as contact info.
  *
  * From webyto point of view could exist many users with the same e-mail address, but on different authentication servers.
  * Having many deployments of the webyto linked to different Keycloak entities, but linked to the same database
  * we can have many same users with the different keycloak id.
  *
  * 
  *
  */

@Component({
  selector: 'app-edit-user-access-tab',
  templateUrl: './edit-user-access-tab.component.html',
  styleUrl: './edit-user-access-tab.component.css',
  encapsulation: ViewEncapsulation.None
})
export class EditUserAccessTabComponent implements OnInit, OnDestroy {

  public userAccessGroup: number = 0;
  public mainCompany: number = 0;

  securityGroups: SecurityGroups[] = [];

  rows: number = 25;
  totalRecords: number = 0;
  loading: boolean = false;

  editing: boolean = false;
  editMode: boolean = false;

  loadGroupsAndRolesQuery: Subscription = null;
  loadUserGroupsAndRoles$ = new Subject<SecurityGroupMembers>();

  userAccessGroups: SecurityGroupMembers[] = [];
  userAccessGroupsClone: SecurityGroupMembers[] = [];
  selectedFilterGroups: SecurityGroupMembers[] = [];

  selectedUserAccessGroups: SecurityGroupMembers = null;
  editedUserAccessGroups: SecurityGroupMembers = null;

  // So far we don't allow to set JSReport admin rights from user access group editing tab
  // due to API issue 
  disallowSetAdminRights: boolean = false;

  // Here we are going in the circle to keep report server URLs as well as report user info
  // to create users on those report servers when new user is created in the system

  // Let's start from the scratch with array of report server URLs
  // We will parse it from global settings
  // Each report server has users and groups to define the access rights
  // User access rights will be mapped from the user access group in the system or set directly by read/edit/admin flags

  // Array of report server URLs

  public reportServerArray:  ReportServer[] = [];
  public reportServerUsers: { [key: string]: any } = {}; // Keep this for internal storage

  // Becase we don't know how many report servers we have
  // therefore we will create array of objects to iterate in template
  //
  // However we have to read users and groups from each report server and store once to reportServerArray object
  // So reportServerArray need to be array of objects like
  // {
  //   serverName: string;
  //   serverUrl: string;
  //   users: any[];
  //   groups: any[];
  //   index: number;
  // }
  //
  // Report users and groups could differ among different servers
  // The key to user is userName (login) on report server
  // This login should match Keycloak userName (e-mail)
  // and userName on report server Could match Keycloak user first and last name

  // SecurityGroupMembers.reportServerUser will keep user object for particular report server
  // and consists of multiple report servers identified by URL as key (or by index)
  // URL indexed object doesn't work in template iteration,
  // therefore we need to create array of objects for template iteration

  // So meaningful idea to have the URL indexed array to servers to return the index and classic array for template iteration.

  // NEW: Add this array for template iteration
  public reportServerAccessList: ReportServerUserAccess[] = [];

  private getServerName(url: string): string {
    // Extract a friendly name from URL or use index
    const match = url.match(/report(\d+)/i);
    return match ? `Report Server ${match[1]}` : url;
  }

  public getReportServerGroups(serverUrl: string): any[] {
    const server = this.reportServerArray.find(rs => rs.serverUrl === serverUrl);
    return server ? server.groups : [];
  }

  public getReportServerUsers(serverUrl: string): any[] {
    const server = this.reportServerArray.find(rs => rs.serverUrl === serverUrl);
    return server ? server.users : [];
  }

  public getReportServerUserShortid(serverUrl: string, userName: string): string {
    const server = this.reportServerArray.find(rs => rs.serverUrl === serverUrl);
    const user = server ? server.users.find(u => u.name.toLowerCase() === userName.toLowerCase()) : null;
    return user ? user.shortid : '';
  }

  public get thereIsNoSelection(): boolean { return this.selectedUserAccessGroups ? false : true; }

  public get canSaveUserInfo(): boolean {
    if (this.editedUserAccessGroups) {
      return (this.editedUserAccessGroups.SGMLOGIN != null && this.editedUserAccessGroups.SGMLOGIN.trim().length > 0
        && (this.userLoginIdExists(this.editedUserAccessGroups))
        && (this.editedUserAccessGroups.SGMPASSWD && this.retypePassword && this.editedUserAccessGroups.SGMPASSWD !== this.retypePassword)
        && !this.sgmExists(this.editedUserAccessGroups));
    }
    return false;
  }

  keycloakUsers: KeycloakUsersList[] = [];

  private keycloakUserMap = new Map<string, KeycloakUsersList>();

  public getKeycloakGroupNamesFromList(keycloakGroupsList: any[]): string {
    return keycloakGroupsList && keycloakGroupsList.length > 0
      ? keycloakGroupsList.map(group => group.name).join(', ')
      : '';
  }
  public userLoginIdExistsOrIsEmpty(sgm: SecurityGroupMembers): boolean {
    //environment.production || console.log('*** EditUserAccessTabComponent userLoginIdExistsOrIsEmpty: ' + sgm.SGMGROUPID + ', ' + sgm.SGMCLIID + ', ' + sgm.SGMPERSID);
    return (sgm.SGMLOGIN == null || sgm.SGMLOGIN.trim().length == 0) ? true : this.userLoginIdExists(sgm);
  }

  public userLoginIdExists(sgm: SecurityGroupMembers): boolean {
    //environment.production || console.log('*** EditUserAccessTabComponent userLoginIdExists: ' + sgm.SGMGROUPID + ', ' + sgm.SGMCLIID + ', ' + sgm.SGMPERSID);

    return this.keycloakUserMap.get(sgm.SGMLOGIN) ? true : false;

//    return (this.keycloakUsers?.find(u => ((u.id == sgm.SGMLOGIN))) ? true : false);
  }

  public sgmExists(sgm: SecurityGroupMembers): boolean {
    //console.log('*** EditUserAccessTabComponent sgmExists: ' + sgm.SGMGROUPID + ', ' + sgm.SGMCLIID + ', ' + sgm.SGMPERSID);
    return (this.userAccessGroups || []).find(uag => (
      (uag.SGMPASSWD != sgm.SGMPASSWD) /* Ignore himself */
      && (
        (uag.SGMGROUPID == sgm.SGMGROUPID) &&
        (uag.SGMCLIID == sgm.SGMCLIID) &&
        (uag.SGMPERSID == sgm.SGMPERSID) &&
        (uag.SGMLOGIN == sgm.SGMLOGIN)
      ))) ? true : false;
  }

  profileToEdit: PerListEntry = null;
  existingUser: boolean = true;

  private securityQuery: Subscription = null;
  private saveSecurityQuery: Subscription = null;
  private personNameQuery: Subscription = null;

  @ViewChild('UAG1') uagTable: Table;

  userFilter: string;
  loginFilter: string;
  descFilter: string;

  keycloakRoles: any[] = [];
  keycloakGroups: any[] = [];

  selectedFilterKeycloakRoles: any[] = [];
  selectedFilterKeycloakGroups: any[] = [];

  retypePassword: string = '';

  demoServerApiText: string = 'This is a demo server API. It is not connected to any real database.';
  userInfoLoaded: boolean = false;

  selectedRow(uag: SecurityGroupMembers): boolean {
    //    return this.selectedUserAccessGroups && this.selectedUserAccessGroups.SGMPASSWD === uag.SGMPASSWD;
    return this.selectedUserAccessGroups && (this.selectedUserAccessGroups === uag);
  }

  toDelete: boolean = false;
  askToDelete: boolean = false;

  doDeleteKeycloakUser: boolean = false;
  doRemoveReportServerUsers: boolean = false;

  /**
   * Check if current user can edit a specific security group member
   * @param esg - SecurityGroupMember to check
   * @returns boolean - true if user can be edited
   */
  private canEditUser(esg: SecurityGroupMembers): boolean {
    const currentUserAccessGroup = this.globalsettingsservice.getAccessGroup();

    environment.production || console.log('*** EditUserAccessTabComponent canEditUser: Checking edit permissions for user ' + esg.SGMLOGIN);
    environment.production || console.log('*** EditUserAccessTabComponent canEditUser: Current user access group: ' + currentUserAccessGroup + ', Target user access group: ' + esg.SGMGROUPID);

    if (!esg) {
      return false;
    }

    // Rule 1: Cannot edit users with equal or higher access group
    if (esg.SGMGROUPID >= currentUserAccessGroup) {
      return false;
    }

    // Rule 2: If user has Keycloak login, check for Keycloak management permissions
    if (esg.SGMLOGIN && !this.authGuard.canManageKeycloakUsers()) {
      return false;
    }

    return true;
  }

  // Check if specific user can be edited
  public canEditUserRow(esg: SecurityGroupMembers): boolean {
    return !this.editing && this.canEditUser(esg);
  }

  // Get tooltip explaining why edit is disabled
  public getEditDisabledTooltip(esg: SecurityGroupMembers): string {
    const currentUserAccessGroup = this.globalsettingsservice.getAccessGroup();

    if (!esg) {
      return '';
    }

    if (esg.SGMGROUPID >= currentUserAccessGroup) {
      return 'Cannot edit users with equal or higher access group';
    }

    if (esg.SGMLOGIN && !this.authGuard.canManageKeycloakUsers()) {
      return 'Requires portal-admin or user-manager role to edit Keycloak users';
    }

    return `Удалить выбранного пользователя ${esg.namestr}`;
  }
constructor(
    private secApi: SecureApiService,
    private globalsettingsservice: GetglobalsettingsService,
    private cliApi: ClientApiService,
    private titleService: Title,
    private filterService: FilterService,
    private helperApi: HelperServerApiServiceService,
    private authGuard: AuthGuard,
    private messageService: MessageService
  ) {

    this.loadGroupsAndRolesQuery = this.loadUserGroupsAndRoles$.pipe(
      tap(ua => {
        environment.production || console.log('*** EditUserAccessTabComponent loadUserGroupsAndRoles$: Entering user: ' + JSON.stringify(ua));
      }),

      filter(ua => (this.userLoginIdExists(ua) && (ua.SGMLOGIN != null) && (ua.SGMLOGIN.trim().length > 0))),   // Allow processing only for users existing in the system

      concatMap(ua => this.cliApi.getPerListEntry(ua.SGMPERSID).pipe(
        tap(pl => environment.production || console.log('*** EditUserAccessTabComponent loadUserGroupsAndRoles$: Retrieved Person entry for SGMPERSID ' + ua.SGMPERSID + ': ' + JSON.stringify(pl))),
        map(pl => {
          if (pl && pl.length > 0) {
            ua.personMap = pl[0];
            ua.namestr = ua.personMap.personSaveAs;
          }
          environment.production || console.log('*** EditUserAccessTabComponent in loadGroupsAndRolesQuery: assign PersonMap: ' + JSON.stringify(ua));

          return ua;
        }),
      )),

      tap(ua => {
        environment.production || console.log('*** EditUserAccessTabComponent loadUserGroupsAndRoles$: Processing user: ' + ua.SGMLOGIN);

        let ku = this.keycloakUsers.find(u => ((u.id == ua.SGMLOGIN)));
        ua.keycloakLocked = ku ? !ku.enabled : null;
      }),

      concatMap(ua => this.helperApi.getObservableKeycloakUserGroups(ua.SGMLOGIN).pipe(
        map(groups => {
          environment.production || console.table(groups);
          //environment.production || console.log(JSON.stringify(groups));
          if (groups && groups.length > 0) {
            ua.keycloakGroups = groups;
          }
          return ua;
        })
      )),

      // Get effective roles (including those from group membership)
      concatMap(ua => this.helperApi.getObservableKeycloakUserEffectiveRoles(ua.SGMLOGIN).pipe(
        map(roles => {
          environment.production || console.table(roles);
          if (roles && roles.length > 0) {
            ua.keycloakRoles = roles;
          }
          return ua;
        })
      )),
    )
      .subscribe({
        next: () => { },
        error: err => {
          console.error('*** EditUserAccessTabComponent in loadUserGroupsAndRoles$: Error: ' + err);
        },
        complete: () => { }
      });
  }

  ngOnDestroy(): void {
    console.log('*** EditUserAccessTabComponent ngOnDestroy: ')

    if (this.securityQuery) { this.securityQuery.unsubscribe(); }
    if (this.saveSecurityQuery) { this.saveSecurityQuery.unsubscribe(); }
    if (this.personNameQuery) { this.personNameQuery.unsubscribe(); }
  }

  loadUserAccessGroupInfo() {
    this.loading = true;

    this.securityQuery =
      this.secApi.getSecurityGroupList().pipe(
          
      // There is no further processing if no security groups
        filter(sgl => sgl && sgl.length > 0),
        tap(sgl => console.log('*** EditUserAccessTabComponent loadUserAccessGroupInfo: Security Groups loaded: ' + sgl.length)),

        // Limit security groups to user's access group
        map(sgl => {
          this.securityGroups = sgl.filter(e => e.SECGROUPID <= this.userAccessGroup);
          this.securityGroups.forEach(sg => { sg.uid = sg.SECGROUPID; sg.cloneIndex = 0; });

          // environment.production || console.table(this.securityGroups);

          return this.globalsettingsservice.getMainCompanyId();
        }),

        concatMap(mc => this.secApi.getUsersAccessGroup(mc).pipe(

          // Looks like no further processing if no user access groups
          // In general this means that very first user should be created using direct db access and then linked to keycloak
          // Otherwise we have no way to create first user in the system
          filter(uag => uag && uag.length > 0),

          tap(uag => {
            console.log('*** EditUserAccessTabComponent loadUserAccessGroupInfo: User Access Groups loaded: ' + uag.length)
            environment.production || console.table(uag);
            this.userAccessGroups = uag;
            this.totalRecords = this.userAccessGroups.length;
          }),

          // Load Keycloak users list and prepare the option list for dropdown
          concatMap(uag => this.helperApi.getObservableKeycloakUsersList().pipe(
            //tap(a => environment.production || console.log('*** EditUserAccessTabComponent getObservableKeycloakUsersList: ' + JSON.stringify(a))),
            //tap(a => environment.production || console.table(a)),

            map(users => {
              this.keycloakUsers = users.map(user => ({ ...user, disabled: !user.enabled }));
              users.forEach(u => this.keycloakUserMap.set(u.id, u));

              return uag;
            }),
          )),

          // Load Keycloak groups and roles for further processing
          concatMap(uag => forkJoin({
            groups: this.helperApi.getObservableKeycloakGroups(),
            roles: this.helperApi.getObservableKeycloakRealmRoles(),
            users: this.helperApi.getObservableKeycloakUsersList()
          }).pipe(
            map(({ groups, roles, users }) => {
              this.keycloakGroups = groups;
              this.keycloakRoles = roles;

              return uag;
            })
          )),

          concatMap(uag => this.helperApi.getObservableKeycloakRealmRoles().pipe(
            map(roles => {
              this.keycloakRoles = roles;
              //environment.production || console.log('*** EditUserAccessTabComponent loadUserAccessGroupInfo: Keycloak Roles loaded: ' + roles.length);
              //environment.production || console.table(this.keycloakRoles);

              return uag;
            }),
            // Walk through Report server list to load users and groups for further processing
          )),

          concatMap(uag => from(this.reportServerArray).pipe(
            concatMap(rsUrl => this.helperApi.getJsReportServerUserGroups(rsUrl).pipe(
              map(groups => {
                environment.production || console.log('*** EditUserAccessTabComponent loadUserAccessGroupInfo: Report server ' + rsUrl.serverName + ' Groups loaded: ' + groups.value.length);
                // Log the groups information (here should was implemented groups.value.length instead of groups.length)
                // environment.production || console.log('*** EditUserAccessTabComponent loadUserAccessGroupInfo: Report server ' + rsUrl + ' Groups loaded: ' + JSON.stringify(groups));
                rsUrl.groups = groups ? groups.value : [];
                environment.production || console.table(rsUrl.groups);
                return rsUrl;
              }),
            )),

            concatMap(rsUrl => this.helperApi.getObservableJsReportUsers(rsUrl.serverUrl).pipe(
              map(users => {
                environment.production || console.log('*** EditUserAccessTabComponent loadUserAccessGroupInfo: Report server ' + rsUrl.serverUrl + ' Users loaded: ' + users.value.length);
                // environment.production || console.table(users.value);

                rsUrl.users = users ? users.value : [];
              }),
            )),

            // like finalize to process all user access groups only once
            // Here we should process each user access group to map security group and person info
            // Map report server access info too
            // and keycloak groups and roles

            last(null),
            defaultIfEmpty(null), // Handle empty source
            map(() => {
              return uag;
            }),
            catchError(err => {
              console.error(`*** EditUserAccessTabComponent Critical error processing JsReportUsers :`, err);
              return of(uag); // Continue pipeline
            }),
          )),
        )),

        // tap(uag => environment.production || console.log('*** EditUserAccessTabComponent loadUserAccessGroupInfo: Processing User Access Groups: ' + uag.length)),
        // tap(() => environment.production || console.table(this.reportServerUsers)),

        concatMap(uag => from(uag).pipe(

          concatMap(ua => this.cliApi.getPerListEntry(ua.SGMPERSID).pipe(
            // tap(pl => environment.production || console.log('*** EditUserAccessTabComponent loadUserAccessGroupInfo: Retrieved Person entry for SGMPERSID ' + ua.SGMPERSID + ': ' + JSON.stringify(pl))),
            map(pl => {
              if (pl && pl.length > 0) {
                ua.personMap = pl[0];
                ua.namestr = ua.personMap.personSaveAs;
              }
              // environment.production || console.log('*** EditUserAccessTabComponent in loadUserAccessGroupInfo: assign PersonMap: ' + JSON.stringify(ua));

              return ua;
            }),
          )),

          map(ua => {
            ua.uid = (ua.SGMPERSID || 1) * (ua.SGMGROUPID || 1) * (ua.SGMCLIID || 2);  // Mimic the Unique Record Id
            ua.group = { ...this.securityGroups.find(sg => sg.SECGROUPID == ua.SGMGROUPID) };

            /*
              * Create more uniq uid key for datatable
              */
            // Very obsolete procedure to create unique SGM password
            // Contemporary approach is to generate random password noone ever knows
            // For fisrt access user should reset his password using "Forgot password" procedure in keycloak login page
            // But to keep backward compatibility we will keep old password generation algorithm

            // Create SGM password if not exists only
            if (!ua.SGMPASSWD) {
              ua.SGMPASSWD = (ua.SGMGROUPID || 1) + '.' + ua.SGMLOGIN + '.' + ua.SGMCLIID + '.' + ua.SGMPERSID;
            }

            ua.cloneIndex = 0;
            ua.editing = false;

            // NEW: Build user-specific report server access list
            // Iterate over report servers and map access
            // If user has no keycloak entry, no Report Server access is defined

            // We need to rewrite this part to map access from reportServerUsers object

            // During user loading we would have 3 different cases for report server and keycloak users:
            // 1) Keycloak user exists, report server user exists - map access from report server user
            // 2) Keycloak user exists, report server user does not exist - no access on report server
            // 3) Keycloak user does not exist - no access on report server

            // Therefore we need to check for each report server whether user exists on that server
            // and map access accordingly
            // If user does not exist on report server, no access is defined

            // First we would map the DB user login to Keycloak user
            // Then we would check for each report server whether user exists

            // If there is no keycloak mapping we would have two options:
            // Login field is empty - we would create initial mapping for report server during user creation
            // If login exists but no keycloak mapping - we would not allow to edit this user
            // because it would be another keycloak server from different deployment

            // Anywaywe would have the report server groups loaded during initialization
            // and we would map user access accordingly

            // Also we would count that userName on report server matches keycloak username (e-mail)
            // But report server operates mainly with shortId for every report server object
            // So would be useful to keep shortId in the mapping too

            let ku = this.keycloakUserMap.get(ua.SGMLOGIN);
            // let ku = this.keycloakUsers.find(u => ((u.id == ua.SGMLOGIN)));

            //environment.production || console.log('*** EditUserAccessTabComponent loadUserAccessGroupInfo: this.keycloakUsers.find: ' + JSON.stringify(ku));
            if (ku) {
              ua.keycloakLocked = !ku.enabled;

              ua.reportServerAccess = this.reportServerArray.map(rsUrl => {

                const rsu = rsUrl.users.find(u =>
                  u.username.toLowerCase() === ku.username.toLowerCase()
                );

                // SecurityGroupMembers contains reportServerArray object with user access info for each report server
                // Each reportServerArray contains the arrays of groups user belongs to on particular report server

                // Also there is reporServer array in the component to iterate through report servers
                // Every row contains all the users and groups with users belong to particular report server

                // During user access group loading we have to map user access on report servers

                // to do so we have to find user on particular report server by his userName (login) and get shortid
                // After that we have to find groups user included by shortid

                return {
                  serverName: rsUrl.serverName,
                  serverUrl: rsUrl.serverUrl,
                  userName:  rsu?.username || '',
                  kcLogin: ku.username,
                  readAccess: rsu?.readAllPermissions || false,
                  editAccess: rsu?.editAllPermissions || false,
                  isAdmin: rsu?.isAdmin || false,
                  userId: rsu?._id || null,
                  shortId: rsu?.shortid || '',
                  enabled: rsu?.enabled !== false,
                  groups: (rsUrl.groups || []).filter(g => g.users.some(u => u.shortid === rsu?.shortid)),
                  reportServerInstance: rsUrl
                } as ReportServerUserAccess;
              });

              //environment.production || console.log('*** EditUserAccessTabComponent loadUserAccessGroupInfo: userInfo after reportServer mapping: ' + JSON.stringify(ua));
              // environment.production || console.table(ua.reportServerAccess);

            } else if (ua.SGMLOGIN != null && ua.SGMLOGIN.trim().length > 0) {
              // User login exists but no keycloak mapping - do nothing and show warning
              environment.production || console.warn('*** EditUserAccessTabComponent loadUserAccessGroupInfo: No Keycloak mapping for user login: ' + ua.SGMLOGIN);
            } else {
              // No user login - new user without keycloak mapping
              environment.production || console.log('*** EditUserAccessTabComponent loadUserAccessGroupInfo: No user login for SGMGROUPID: ' + ua.SGMGROUPID + ', SGMPERSID: ' + ua.SGMPERSID);

              // Create default report server access with no access rights
              // Take into account that name shouldn't be empty

              // The actual details will be available during user editing

              // In margin case -user could be already exists on report server without keycloak mapping
              // We have to fully rewrite his right accordingly to user access group

              ua.reportServerAccess = this.reportServerArray.map(rsUrl => ({
                serverName: rsUrl.serverName,
                serverUrl: rsUrl.serverUrl,
                // need to get user details from personMap if exists
                userName: ua.personMap?.PERSONEMAIL?.toLowerCase() || '',
                name: ua.personMap?.personSaveAs || ua.SGMLOGIN,
                kcLogin: '',
                readAccess: false,
                editAccess: false,
                isAdmin: false,
                reportServerInstance: rsUrl
              } as ReportServerUserAccess));
            }
            return ua;
          }),
          filter(ua => this.userLoginIdExists(ua)),   // Allow processing only for users existing in the system
          //tap(ua => environment.production || console.log('*** EditUserAccessTabComponent loadUserAccessGroupInfo: Processing user: ' + ua.SGMLOGIN)),

          filter(ua => ua.SGMLOGIN?.trim().length > 0),

          concatMap(ua => this.helperApi.getObservableKeycloakUserGroups(ua.SGMLOGIN).pipe(
            map(groups => {
              if (groups?.length > 0) {
                ua.keycloakGroups = groups;
              }
              return ua;
            })
          )),

          // Get effective roles including those from group membership
          concatMap(ua => this.helperApi.getObservableKeycloakUserEffectiveRoles(ua.SGMLOGIN).pipe(
            map(roles => {
              //environment.production || console.table(roles);
              if (roles?.length > 0) {
                ua.keycloakRoles = roles;
              }
              return ua;
            })
          )),
        )),

        finalize(() => {
          this.userInfoLoaded = true;
          this.loading = false;
        })
      )
        .subscribe({
          next: () => { },
          error: err => {
            console.error('*** EditUserAccessTabComponent loadUserAccessGroupInfo: Error: ' + err);
            this.loading = false;
            this.messageService.add(err?.status === 403
              ? { severity: 'error', summary: 'Недостаточно прав', detail: 'Требуется роль portal-admin для управления пользователями доступа' }
              : { severity: 'error', summary: 'Не удалось загрузить список пользователей', detail: err?.message || 'Unknown error' });
          },
          complete: () => { }

        });
  }

  ngOnInit(): void {
    console.log('*** EditUserAccessTabComponent onInit');
    this.titleService.setTitle('Группы доступа - редактирование пользователей');

    this.globalsettingsservice.getGlobalSettings();

    this.filterService.register('inCollection', (value: number, filter: SecurityGroups[]) => (filter.find(e => e.SECGROUPID == value)));

    this.userAccessGroup = this.globalsettingsservice.getAccessGroup();

    this.mainCompany = this.globalsettingsservice.getMainCompanyId();

    const reportServerUrl = this.globalsettingsservice.getReportServerUrl();
    const reportServerUrls = reportServerUrl ? [reportServerUrl] : [];

    environment.production || console.log('*** EditUserAccessTabComponent in ngOnInit getReportServers: ' + JSON.stringify(reportServerUrls));
    // Parse report server URLs from JSON string array like
    // ["{"report1":"https://AUTH2.DISTI.PRO:8589", "report2":"https://AUTH2.DISTI.PRO:8489", "apiUrl": "/api/report/", "secret": "YWRtaW46anNBYnJ1cHQ0IQ=="}"]
    // Here we know the report servers are JSreport servers and we need to create users on those servers too

    // Users and groups will be loaded later during user access group loading

    if (reportServerUrl != '') {
      try {
        const parsedConfig = JSON.parse(reportServerUrl);
        Object.keys(parsedConfig).forEach((key, i) => {
          if (key.startsWith('report')) {
            const reportServer: ReportServer = {
              serverName: key,
              serverUrl: parsedConfig[key].toLowerCase(),
              users: [],
              groups: [],
              index: i
            };
            this.reportServerArray.push(reportServer);
          }
        });
      } catch (error) {
        console.error('*** EditUserAccessTabComponent: Error parsing report server URLs', error);
      }
      environment.production || console.log('*** EditUserAccessTabComponent in ngOnInit getReportServers: ' + JSON.stringify(this.reportServerArray));
    }

    this.loadUserAccessGroupInfo();
  }

  onReset() {
    console.log('*** EditUserAccessTabComponent onReset')
  }

  onRowSelect(selectedId) {
    // environment.production || console.log('*** EditUserAccessTabComponent in onRowSelect: ' + JSON.stringify(selectedId));
  }

  onRowUnselect(selectedId) {
    // environment.production || console.log('*** EditUserAccessTabComponent in onRowUnselect: ' + JSON.stringify(selectedId));
  }

  onUserLoginChange(event) {
    console.log('*** EditUserAccessTabComponent in onUserLoginChange: ' + JSON.stringify(event));
    //if (this.uagTable && this.uagTable.filters) {
    //  for (let f in this.uagTable.filters) {
    //    console.log('*** DocumentsComponent table filter: ' + f);
    //  }
    //}
  }

  /* * Event issued on dropdown list opening
  * * @param event - the event object containing the selected group
  * * @returns void
  */
  groupEditChange(event) {
    console.log('*** EditUserAccessTabComponent in groupEditChange: ' + JSON.stringify(event));
    /* syncronize settings after changes.
     * For update purposes api will use infomation from clone (shadow copy)
     */
    if (this.editedUserAccessGroups) {
      this.editedUserAccessGroups.group = event.value;
      this.editedUserAccessGroups.SGMGROUPID = this.editedUserAccessGroups.group.SECGROUPID;
      this.editedUserAccessGroups = { ...this.editedUserAccessGroups }; // Trigger the change detection
      environment.production || console.log('*** EditUserAccessTabComponent in groupEditChange selection: ' + JSON.stringify(this.editedUserAccessGroups));
    }
  }

  onUserDescChange(event) {
    console.log('*** EditUserAccessTabComponent in onUserDescChange: ' + JSON.stringify(event));
  }

  mainPersonSelected(event) {
    console.log('*** EditUserAccessTabComponent in mainPersonSelected event: ' + JSON.stringify(event));
    environment.production || console.log('*** EditUserAccessTabComponent in mainPersonSelected selection: ' + JSON.stringify(this.editedUserAccessGroups));

    if (this.editedUserAccessGroups) {
      this.loading = true;
      this.editedUserAccessGroups.SGMPERSID = event;

      this.cliApi.getPerListEntry(this.editedUserAccessGroups.SGMPERSID).pipe(
        filter(data => data && data.length > 0),
        map(data => data[0]),
        map(data => {
          this.editedUserAccessGroups.personMap = data;
          this.editedUserAccessGroups.namestr = data.personSaveAs;

          this.editedUserAccessGroups = { ...this.editedUserAccessGroups }; // Trigger the change detection

          this.profileToEdit = this.editedUserAccessGroups.personMap;
        }),

        finalize(() => {
          environment.production || console.log('*** EditUserAccessTabComponent in mainPersonSelected finalize: ' + JSON.stringify(this.editedUserAccessGroups));
          this.userInfoLoaded = true;
          this.loading = false;
        })
      ).subscribe({
        next: () => {
          this.loadUserGroupsAndRoles$.next(this.editedUserAccessGroups);
        },
        error: err => {
          console.error('*** EditUserAccessTabComponent in onUagRowEditSave: Error: ' + err);
        },
        complete: () => { }
      });
    }
    //environment.production || console.table(this.selectedUserAccessGroups);
  }

  // looks like we have to reread the list of keycloak users first to check whether user exists

  onUserUpdate(user: SecurityGroupMembers) {
    console.log('*** EditUserAccessTabComponent onUserUpdate: ' + JSON.stringify(user));

    const keycloakUsersSub = this.helperApi.getObservableKeycloakUsersList().pipe(
      //tap(a => environment.production || console.log('*** EditUserAccessTabComponent onUserUpdate getObservableKeycloakUsersList: ' + JSON.stringify(a))),
      //tap(a => environment.production || console.table(a)),
      map(users => {
//        this.keycloakUsers = users.map(user => ({ ...user, disabled: !user.enabled }));
        return users;
      }),
      finalize(() => {
        keycloakUsersSub.unsubscribe();
      })
    ).subscribe({
      next: () => {

      },
      error: err => {
        console.error('*** EditUserAccessTabComponent in onUserUpdate: Error: ' + err);
      },
      complete: () => {
        this.finalizeUserUpdate(user);
      }
    });
  }

  // Final step of user update after keycloak users are reloaded

  finalizeUserUpdate(user: SecurityGroupMembers) {
    environment.production || console.log('*** EditUserAccessTabComponent finalizeUserUpdate: ' + JSON.stringify(user))
    if (this.editedUserAccessGroups) {
      this.editedUserAccessGroups.SGMLOGIN = user.SGMLOGIN;

      let ku = this.keycloakUserMap.get(user.SGMLOGIN);
      if (ku) {
        this.editedUserAccessGroups.reportServerAccess.forEach(u => u.userName = ku.username);
      }

      // Reread groups and roles for the user (???? needed ???)
      // this.loadUserGroupsAndRoles$.next(this.editedUserAccessGroups);
    }
    environment.production || console.log('*** EditUserAccessTabComponent finalizeUserUpdate: editedUserAccessGroups: ' + JSON.stringify(this.editedUserAccessGroups));
  }

  filterUagTable(event) {
    environment.production || console.log('*** EditUserAccessTabComponent in filterUagTable: ' + JSON.stringify(event));
  }

  onGroupListChange(event) {
    console.log('*** EditUserAccessTabComponent in onGroupListChange: ' + JSON.stringify(event));
    //environment.production || console.table(event.value);

    this.uagTable.filter(event.value, 'SGMGROUPID', 'inCollection');
  }

  onKeycloakGroupListChange(event) {
    console.log('*** EditUserAccessTabComponent in onKeycloakGroupListChange: ' + JSON.stringify(event));
    //environment.production || console.table(event.value);

    this.uagTable.filter(event.value, 'SGMGROUPID', 'inCollection');
  }

  onKeycloakRolesListChange(event) {
    console.log('*** EditUserAccessTabComponent in onKeycloakRolesListChange: ' + JSON.stringify(event));
    //environment.production || console.table(event.value);

    this.uagTable.filter(event.value, 'SGMGROUPID', 'inCollection');
  }

  onNewUserAccess(event) {
    console.log('*** EditUserAccessTabComponent in onNewUserAccess: ' + JSON.stringify(event));

    const newUserAccessGroup: number = 1;

    this.totalRecords = this.userAccessGroups.unshift({
      uid: -2,

      ...SecurityGroupMembers.mapGlobalSettings(this.globalsettingsservice.getGlobalSettings()),

      SGMGROUPID: newUserAccessGroup,
      SGMCLIID: this.globalsettingsservice.getMainCompanyId(),
      SGMPERSID: null /* this.globalsettingsservice.getGlobalSettings().mainPersonId */,
      SGMDESC: 'New User with no access',
      SGMLOGIN: null,
      SGMPASSWD: null,
      group: { ...this.securityGroups.find(sg => sg.SECGROUPID == newUserAccessGroup) },
      reportServerAccess: this.reportServerArray.map(server => ({
        serverName: `Report Server ${server.index}`,
        serverUrl: server.serverUrl,
        userName: 'New User',
        password: 'passwordForNewUser$',
        kcLogin: '',
        readAccess: true,
        editAccess: false,
        isAdmin: false
      })),
      cloneIndex: 0,
      editing: true
    });
    this.onUagRowEditInit(this.userAccessGroups[0]);
    this.uagTable.initRowEdit(this.userAccessGroups[0]);
  }

  onCopyUserAccess(event) {
    console.log('*** EditUserAccessTabComponent in onCopyUserAccess: ' + JSON.stringify(event));

    let userCopy: SecurityGroupMembers = {
      ...this.selectedUserAccessGroups,

      uid: -2,

      SGMLOGIN: this.selectedUserAccessGroups.SGMLOGIN + '(1)',

      SGMPASSWD: this.selectedUserAccessGroups.SGMGROUPID + '.' +
        this.selectedUserAccessGroups.SGMLOGIN + '(1)' + '.' +
        this.selectedUserAccessGroups.SGMCLIID + '.' +
        this.selectedUserAccessGroups.SGMPERSID
    };

    this.userAccessGroups = [...[userCopy], ...this.userAccessGroups];

    this.onUagRowEditInit(this.userAccessGroups[0]);
    this.uagTable.initRowEdit(this.userAccessGroups[0]);
  }

  cancelToDelete() {
    this.toDelete = false;
    this.askToDelete = false;
  }

  // First step of user deletion - mark for deletion and ask for confirmation

  deleteUserAccess(event) {
    console.log('*** EditUserAccessTabComponent in deleteUserAccess: ' + JSON.stringify(event));
    this.toDelete = true;
  }

  // Second step of user deletion - ask for confirmation
  confirmToDelete(uag: SecurityGroupMembers) {
    console.log('*** EditUserAccessTabComponent in confirmToDelete: ' + JSON.stringify(uag));
    this.toDelete = false;
    this.askToDelete = true;
  }

  iAmSureToDelete(uag: SecurityGroupMembers) {

    console.log('*** EditUserAccessTabComponent in iAmSureToDelete: ' + JSON.stringify(uag));

    // Here we need to change the permission on database level to enable user deletion
    // Also we need additional confirmation from user to delete user in Keycloak and report servers

    this.askToDelete = false;
    this.toDelete = false;

    //  Here very old and very strange logic to find user by password string
    // Because password is (would be) unique for every user
    // While login could be same for different clients (companies) in multi-tenant environment
    // Also user couldn't have login at all (null or empty string) - in this case user is not mapped to keycloak (or other auth server)

    if (uag) {
      const index = this.userAccessGroups.findIndex(sg => sg.SGMPASSWD == uag.SGMPASSWD);
      if (index >= 0) {
        this.loading = true;

        this.secApi.deleteUserSecuritySettings(uag).pipe(
          tap(response => console.log('*** EditUserAccessTabComponent in iAmSureToDelete: deleteUserSecuritySettings response: ' + JSON.stringify(response))),

          concatMap(response => {
            const deleteUserObservable: Observable<any>[] = [];

            if (response?.[0] > 0) {
              console.log(`✓ User access group record for user ${uag.SGMLOGIN} deleted from database`)

              // At least one record deleted from user access groups table
              // Now try to Delete from Keycloak

              if (this.userLoginIdExists(uag)) {
                if (this.doDeleteKeycloakUser) {
                  environment.production || console.log('*** EditUserAccessTabComponent in iAmSureToDelete: Deleting user from keycloak servers: ');
                  deleteUserObservable.push(this.helperApi.deleteKeycloakUser(uag.SGMLOGIN).pipe(
                    tap(() => console.log(`✓ Keycloak user ${uag.SGMLOGIN} deleted`)),
                    catchError(err => {
                      console.error(`✗ Error deleting Keycloak user ${uag.SGMLOGIN}:`, err);
                      return of(null);
                    })
                  ));
                }

                // Delete from Report Servers ✅ FIXED

                if (this.doRemoveReportServerUsers && (this.reportServerArray.length > 0) && (uag.reportServerAccess?.length > 0)) {
                  environment.production || console.log('*** EditUserAccessTabComponent in iAmSureToDelete: Deleting user from report servers: ' + JSON.stringify(uag.reportServerAccess));

                  uag.reportServerAccess.forEach(serverAccess => {
                    if (serverAccess.userId) {
                      const jsReportUser: JsReportUser = {
                        _id: serverAccess.userId,
                        username: serverAccess.userName,
                        shortid: serverAccess.shortId,
                        name: serverAccess.name || serverAccess.userName,
                        readAllPermissions: serverAccess.readAccess,
                        editAllPermissions: serverAccess.editAccess,
                        isAdmin: serverAccess.isAdmin
                      };

                      deleteUserObservable.push( this.helperApi.deleteObservableJsReportUser(serverAccess, jsReportUser) );
                    }
                  });
                }
              }
            }
            return deleteUserObservable.length > 0 ? forkJoin(deleteUserObservable) : of([]);
          }),

          finalize(() => {
            this.loading = false;
            this.loadUserAccessGroupInfo();
          })
        ).subscribe({
          error: err => {
            console.error('*** EditUserAccessTabComponent in deleteUserAccess: Error: ' + err);
            this.loading = false;
          },
        });
      }
    }
  }

  onUagRowEditInit(esg: SecurityGroupMembers) {
    console.log('*** EditUserAccessTabComponent in onUagRowEditInit: ');
    //environment.production || console.table(esg);

    // Check if current user has permission to edit this user
    if (!this.canEditUser(esg)) {
      console.warn('*** EditUserAccessTabComponent: Insufficient permissions to edit user');
      alert('You do not have sufficient permissions to edit this user');
      return;
    }

    let newCloneIndex: number = 0;
    this.editedUserAccessGroups = esg;

    this.retypePassword = esg.SGMPASSWD;

    this.editMode = true;

    /*
     * Quite tricky algoritm:
     * Because we can edit simultaneously more than one row in the table.
     * Therefore for every edited row we have to create cloned copy in shadow array [securityGroupsClone]
     * We can't predict the order of starting and ending the edit process
     * 
     * So - Search for max cloneIndex in current table and assign +1 to newly created row copy
     *
     * When row either cancelled or saved - clone index resetted to 0, clone copy deleted from shadow array
     *
     */
    this.userAccessGroupsClone.forEach(uag => { newCloneIndex = Math.max(newCloneIndex, uag.cloneIndex) });

    esg.cloneIndex = ++newCloneIndex;
    //environment.production || console.log('*** EditUserAccessTabComponent in onUagRowEditInit: newCloneIndex is: ' + newCloneIndex);
    //environment.production || console.log('*** EditUserAccessTabComponent in onUagRowEditInit: ' + JSON.stringify(esg));
    //environment.production || console.log('*** EditUserAccessTabComponent in onUagRowEditInit: ' + JSON.stringify(this.editedUserAccessGroups));

    this.userAccessGroupsClone.push({ ...esg });
    esg.editing = true;
  }

  onUagRowEditCancel(esg: SecurityGroupMembers, index: number) {
    console.log('*** EditUserAccessTabComponent in onUagRowEditCancel: ' + index);
    //environment.production || console.table(esg);
    this.editedUserAccessGroups = null;

    if (esg.uid == -2) {
      // Cancel the newly created record
      // simply delete

      this.userAccessGroups.splice(index, 1);

    } else {
      // Cancel editing of existing record
      // have to restore from cloned copy

      this.userAccessGroups[index] = { ...this.userAccessGroupsClone.find(sg => sg.cloneIndex == esg.cloneIndex) };
      this.userAccessGroups[index].cloneIndex = 0;
    }

    this.userAccessGroupsClone.splice(this.userAccessGroupsClone.findIndex(sg => sg.cloneIndex == esg.cloneIndex), 1);

    this.editMode = false;
  }

  onUagRowEditSave(esg: SecurityGroupMembers) {
    console.log('*** EditUserAccessTabComponent in onUagRowEditSave: ');
    environment.production || console.log('*** EditUserAccessTabComponent in onUagRowEditSave: ' + JSON.stringify(esg));
    //environment.production || console.log('*** EditUserAccessTabComponent in onUagRowEditSave: ' + JSON.stringify(this.editedUserAccessGroups));

    //environment.production || console.table(esg);
    // Post update
    // Earn the original security group in case we would change it for particular user
    // UPDATE: We can change the user also
    // UPDATE2: This doesn't work. Both esg and clone has the same group id.

    esg.SGMPASSWD = (esg.SGMGROUPID || 1) + '.' + esg.SGMLOGIN + '.' + esg.SGMCLIID + '.' + esg.SGMPERSID;
    this.loading = true;
    this.saveSecurityQuery =
      concat(((esg.uid == -2) ?
        this.secApi.putUserSecuritySettings(esg) :
        this.secApi.postUserSecuritySettings({ ... this.userAccessGroupsClone.find(sg => sg.cloneIndex == esg.cloneIndex) }, esg)
      ))
        .pipe(
          tap(count => {
            environment.production || console.log('*** EditUserAccessTabComponent in onUagRowEditSave: onMapSave result: ' + count);
          }),

          // Remove the clone copy from shadow array
          map(() => {
            environment.production || console.log('*** EditUserAccessTabComponent in onUagRowEditSave: onSubcsribe to Save: ');

            this.userAccessGroupsClone.splice(this.userAccessGroupsClone.findIndex(sg => sg.cloneIndex == esg.cloneIndex), 1);
            esg.cloneIndex = 0;

            esg.uid = esg.SGMGROUPID * esg.SGMCLIID;
            esg.group = { ...this.securityGroups.find(sg => sg.SECGROUPID == esg.SGMGROUPID) };
            esg.editing = false;

            return esg;
          }),

          tap(esg => environment.production || console.log('*** EditUserAccessTabComponent in onUagRowEditSave: after Save: ' + JSON.stringify(esg))),

          // Looks like redundant - but we need to be sure we have latest person name (really?)
          //
          concatMap(esg => this.cliApi.getPerListEntry(esg.SGMPERSID).pipe(
            tap(a => environment.production || console.log('*** EditUserAccessTabComponent in onUagRowEditSave: Retrieved Person entry for SGMPERSID ' + esg.SGMPERSID + ': ' + JSON.stringify(a))),
            map(data => {
              if (data && data.length > 0) {
                esg.namestr = data[0].personSaveAs;
              }
              //environment.production || console.log('*** EditUserAccessTabComponent in onUagRowEditSave: assign groups: ' + JSON.stringify(esg));

              return esg;
            }),
          )),

          // Locked user can't be updated in Keycloak - so we need to unlock first if needed
          // map(esg => { esg.keycloakLocked = false; return esg; }),    // Unlock user by default

          // Check whether we could move forth with keycloak user processing
          filter(esg => this.keycloakUsers.find(e => e.id == esg.SGMLOGIN) != null),

          tap(esg => environment.production || console.log('*** EditUserAccessTabComponent in onUagRowEditSave: after keycloakUsers.find: ' + JSON.stringify(esg))),
          // Update Keycloak user roles
          concatMap(esg => this.helperApi.postObservableKeycloakUserRoles(esg.SGMLOGIN, esg.keycloakRoles || []).pipe(
            map(() => esg)
          )),

          tap(esg => environment.production || console.log('*** EditUserAccessTabComponent in onUagRowEditSave: after postObservableKeycloakUserRoles: ' + JSON.stringify(esg))),

          // Here we need to check whether user was assigned to group now not assigned anymore
          // So first remove from all groups, then add to selected groups
          concatMap((esg: SecurityGroupMembers) => {
            // Early return if no groups
            if (!esg.keycloakGroups?.length) {
              environment.production || console.log(`*** EditUserAccessTabComponent in onUagRowEditSave: No Keycloak groups for user ${esg.SGMLOGIN}`);
              return of(esg);
            }

            return from(esg.keycloakGroups).pipe(
              concatMap(keycloakGroup =>
                this.helperApi.deleteObservableKeycloakUserFromGroup(esg.SGMLOGIN, keycloakGroup.id).pipe(
                  tap(() => environment.production || console.log(`*** EditUserAccessTabComponent in onUagRowEditSave: ✓ Removed user from group: ${keycloakGroup.name}`)),
                  catchError(err => {
                    console.error(`*** EditUserAccessTabComponent in onUagRowEditSave: ✗ Failed to remove user ${esg.SGMLOGIN} from group ${keycloakGroup.name}:`, err);
                    return of(null); // Continue despite individual failures
                  })
                )
              ),
              defaultIfEmpty(null), // Handle empty source
              toArray(), // Wait for all operations
              map((results) => {
                const successCount = results.filter(r => r !== null).length;
                environment.production || console.log(`Added user to ${successCount}/${esg.keycloakGroups.length} groups`);
                return esg;
              }),
              catchError(err => {
                console.error(`Critical error processing Keycloak groups for ${esg.SGMLOGIN}:`, err); 
                return of(esg); // Continue pipeline
              })
            );
          }),

          tap(esg => environment.production || console.log('*** EditUserAccessTabComponent in onUagRowEditSave: after deleteObservableKeycloakUserFromGroup: ' + JSON.stringify(esg))),

          // Process keycloak groups out of person namestr
          concatMap((esg: SecurityGroupMembers) => {
            // Early return if no groups
            if (!esg.keycloakGroups?.length) {
              environment.production || console.log(`*** EditUserAccessTabComponent in onUagRowEditSave: No Keycloak groups for user ${esg.SGMLOGIN}`);
              return of(esg);
            }

            return from(esg.keycloakGroups).pipe(
              concatMap(keycloakGroup =>
                this.helperApi.putObservableKeycloakUserToGroup(esg.SGMLOGIN, keycloakGroup.id).pipe(
                  tap(() => environment.production || console.log(`*** EditUserAccessTabComponent in onUagRowEditSave: ✓ Added user to group: ${keycloakGroup.name}`)),
                  catchError(err => {
                    console.error(`*** EditUserAccessTabComponent in onUagRowEditSave: ✗ Failed to add user ${esg.SGMLOGIN} to group ${keycloakGroup.name}:`, err);
                    return of(null); // Continue despite individual failures
                  })
                )
              ),
              defaultIfEmpty(null), // Handle empty source
              toArray(), // Wait for all operations
              map((results) => {
                const successCount = results.filter(r => r !== null).length;
                environment.production || console.log(`Added user to ${successCount}/${esg.keycloakGroups.length} groups`);
                return esg;
              }),
              catchError(err => {
                console.error(`Critical error processing Keycloak groups for ${esg.SGMLOGIN}:`, err);
                return of(esg); // Continue pipeline
              })
            );
          }),

          tap(esg => environment.production || console.log('*** EditUserAccessTabComponent in onUagRowEditSave: after putObservableKeycloakUserToGroup: ' + JSON.stringify(esg))),

          // here we need to add user to report servers too
          concatMap((esg: SecurityGroupMembers) => from(esg.reportServerAccess).pipe(
            tap(server => {

              // Here we need to check if user exists on report server first
              // If not - we need to fill in userName, name and password for new user creation
              // and than call setupJsReportUserPermissions where we will check for user existence again

              // the simplest check is whether server.userName is filled in
              tap(server => environment.production || console.log('*** EditUserAccessTabComponent in onUagRowEditSave: in from(esg.reportServerAccess): ' + JSON.stringify(server)));

              if (!server.userName) {
                const ku = this.keycloakUsers.find(e => e.id == esg.SGMLOGIN);
                if (!ku) {
                  throw new Error('Keycloak user not found for SGMLOGIN: ' + esg.SGMLOGIN);
                }
                server.userName = ku.username;
                server.kcLogin =  ku.username;
                server.password = esg.SGMPASSWD;
              }
            }),

            concatMap(server => this.helperApi.setupJsReportUserPermissions(server)),
            tap(server => environment.production || console.log('*** EditUserAccessTabComponent in onUagRowEditSave: Report server to group ' + JSON.stringify(server))),
            filter(server => server !== null && server !== undefined),
            concatMap(server => this.helperApi.setupJsReportUserGroups(server))
          )),

          finalize(() => {
            this.editedUserAccessGroups = null;
            this.editMode = false;
            this.loading = false;
            this.loadUserAccessGroupInfo();
          })
        )
        .subscribe({
          next: () => { },
          error: err => {
            console.error('*** EditUserAccessTabComponent in onUagRowEditSave: Error: ' + err);
            this.editMode = false;
            this.loading = false;
            this.loadUserAccessGroupInfo();
          },
          complete: () => {
          }
        });
  }

  onStateSave(state) {
    //    console.log('*** EditUserAccessTabComponent onStateSave: ' + JSON.stringify(state));
  }

  onStateRestore(state) {
    console.log('*** EditUserAccessTabComponent onStateRestore: ' + JSON.stringify(state));
    if (state.filters) {
      for (let f in state.filters) {
        switch (f) {
          case 'SGMGROUPID':
            this.selectedFilterGroups = state.filters[f].value;
            true || environment.production || console.table(state.filters[f].value);
            break;

          case 'namestr':
            this.userFilter = state.filters[f].value;
            break;

          case 'SGMLOGIN':
            this.loginFilter = state.filters[f].value;
            break;

          case 'SGMDESC':
            this.descFilter = state.filters[f].value;
            break;
        }
      }
    }
  }

  reloadTable(event) {
    this.loadUserAccessGroupInfo();
  }

  clearOrResetTableFilter(event) {
    this.uagTable.clear();

    this.selectedFilterGroups = [];
    this.userFilter =
      this.loginFilter =
      this.descFilter = '';

    this.uagTable.clearState();
  }

  onUserKeycloakGroupListChange(event) {
    console.log('*** EditUserAccessTabComponent in onUserKeycloakGroupListChange: ' + JSON.stringify(event));
  }

  onUserKeycloakRolesListChange(event) {
    console.log('*** EditUserAccessTabComponent in onUserKeycloakRolesListChange: ' + JSON.stringify(event));
  }

  cancelRowEdit() {
    if (this.editedUserAccessGroups) {
      const index = this.userAccessGroups.findIndex(sg => sg.cloneIndex == this.editedUserAccessGroups.cloneIndex);
      this.onUagRowEditCancel(this.editedUserAccessGroups, index);
    //  this.loadUserAccessGroupInfo();
    }
  }

  saveRowEdit() {
    if (this.editedUserAccessGroups) {
      this.onUagRowEditSave(this.editedUserAccessGroups);
    }
  }

  onServerAccessChange(server: ReportServerUserAccess, index: number) {
    console.log('*** EditUserAccessTabComponent in onServerAccessChange: server (index= ' + index + ') : ' + JSON.stringify(server));

    server.reportServerInstance = this.reportServerArray.find(rs => rs.serverUrl == server.serverUrl);

    if (this.editedUserAccessGroups) {

      // Update the edited user's report server access
      if (this.editedUserAccessGroups.reportServerAccess) {
        this.editedUserAccessGroups.reportServerAccess[index] = { ...server };
      }
    }
  }
  reportServerUserGroupsChange(event) {
    console.log('*** EditUserAccessTabComponent in userGroupsChange: ' + JSON.stringify(event));

    if (this.editedUserAccessGroups) {
    //  if (this.editedUserAccessGroups.reportServerAccess) {
    //    this.editedUserAccessGroups.reportServerAccess[index] = { ...server };
    //  }
    }
  }
}

/*
// jsreport API - Create User for Keycloak Authentication

// ==============================================
// 1. Create User for Keycloak Auth (fetch)
// ==============================================

async function createKeycloakUser() {
  const url = 'http://localhost:5488/odata/users';
  
  // For Keycloak authentication, password is not needed
  // The username should match the Keycloak username/email
  const userData = {
    username: 'keycloak.user@example.com', // Must match Keycloak username
    // No password field - Keycloak handles authentication
    isAdmin: false,
    // Optional: You can add custom properties
    enabled: true
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Basic ' + btoa('admin:password') // Admin credentials
      },
      body: JSON.stringify(userData)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`HTTP error! status: ${response.status}, ${JSON.stringify(errorData)}`);
    }

    const result = await response.json();
    console.log('Keycloak user created in jsreport:', result);
    return result;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
}

// ==============================================
// 2. Using axios
// ==============================================

const axios = require('axios');

async function createKeycloakUserAxios() {
  const url = 'http://localhost:5488/odata/users';
  
  const userData = {
    username: 'keycloak.user@example.com',
    isAdmin: false
  };

  try {
    const response = await axios.post(url, userData, {
      auth: {
        username: 'admin',
        password: 'password'
      },
      headers: {
        'Content-Type': 'application/json'
      }
    });

    console.log('Keycloak user created:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error:', error.response?.data || error.message);
    throw error;
  }
}

// ==============================================
// 3. Bulk User Creation from Keycloak
// ==============================================

async function syncKeycloakUsers(keycloakUsers) {
  const url = 'http://localhost:5488/odata/users';
  const adminAuth = 'Basic ' + btoa('admin:password');
  
  const results = [];
  
  for (const keycloakUser of keycloakUsers) {
    const userData = {
      username: keycloakUser.email || keycloakUser.username,
      isAdmin: keycloakUser.roles?.includes('admin') || false,
      enabled: keycloakUser.enabled
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': adminAuth
        },
        body: JSON.stringify(userData)
      });

      if (response.ok) {
        const result = await response.json();
        results.push({ success: true, user: result });
        console.log(`✓ Created user: ${userData.username}`);
      } else {
        const error = await response.json().catch(() => ({}));
        results.push({ success: false, username: userData.username, error });
        console.log(`✗ Failed to create user: ${userData.username}`);
      }
    } catch (error) {
      results.push({ success: false, username: userData.username, error: error.message });
      console.error(`✗ Error creating user ${userData.username}:`, error.message);
    }
  }
  
  return results;
}

// ==============================================
// 4. Check if User Exists Before Creating
// ==============================================

async function createKeycloakUserIfNotExists(username, isAdmin = false) {
  const baseUrl = 'http://localhost:5488';
  const adminAuth = 'Basic ' + btoa('admin:password');
  
  try {
    // Check if user already exists
    const checkUrl = `${baseUrl}/odata/users?$filter=username eq '${username}'`;
    const checkResponse = await fetch(checkUrl, {
      headers: { 'Authorization': adminAuth }
    });
    
    if (!checkResponse.ok) {
      throw new Error(`Failed to check user existence: ${checkResponse.status}`);
    }
    
    const existingUsers = await checkResponse.json();
    
    if (existingUsers.value && existingUsers.value.length > 0) {
      console.log(`User ${username} already exists`);
      return { exists: true, user: existingUsers.value[0] };
    }
    
    // User doesn't exist, create it
    const createUrl = `${baseUrl}/odata/users`;
    const userData = {
      username: username,
      isAdmin: isAdmin
    };
    
    const createResponse = await fetch(createUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': adminAuth
      },
      body: JSON.stringify(userData)
    });
    
    if (!createResponse.ok) {
      const error = await createResponse.json().catch(() => ({}));
      throw new Error(`Failed to create user: ${JSON.stringify(error)}`);
    }
    
    const newUser = await createResponse.json();
    console.log(`✓ Created new user: ${username}`);
    return { exists: false, user: newUser };
    
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
}

// ==============================================
// 5. Complete Example with Error Handling
// ==============================================

async function addKeycloakUserToJsreport(config) {
  const {
    jsreportUrl = 'http://localhost:5488',
    adminUsername = 'admin',
    adminPassword = 'password',
    keycloakUsername, // Email or username from Keycloak
    isAdmin = false,
    checkExisting = true
  } = config;

  if (!keycloakUsername) {
    throw new Error('keycloakUsername is required');
  }

  const baseUrl = jsreportUrl.replace(/\/$/, ''); // Remove trailing slash
  const adminAuth = 'Basic ' + btoa(`${adminUsername}:${adminPassword}`);

  try {
    // Optional: Check if user already exists
    if (checkExisting) {
      const checkUrl = `${baseUrl}/odata/users?$filter=username eq '${keycloakUsername}'`;
      const checkResponse = await fetch(checkUrl, {
        headers: { 'Authorization': adminAuth }
      });

      if (checkResponse.ok) {
        const existing = await checkResponse.json();
        if (existing.value && existing.value.length > 0) {
          console.log(`ℹ User ${keycloakUsername} already exists in jsreport`);
          return { created: false, existing: true, user: existing.value[0] };
        }
      }
    }

    // Create the user
    const createUrl = `${baseUrl}/odata/users`;
    const userData = {
      username: keycloakUsername,
      isAdmin: isAdmin
    };

    const createResponse = await fetch(createUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': adminAuth
      },
      body: JSON.stringify(userData)
    });

    if (!createResponse.ok) {
      const errorData = await createResponse.json().catch(() => ({}));
      throw new Error(
        `Failed to create user: ${createResponse.status} ${createResponse.statusText}. ${
          JSON.stringify(errorData)
        }`
      );
    }

    const result = await createResponse.json();
    console.log('✓ Keycloak user successfully added to jsreport:', {
      username: result.username,
      _id: result._id,
      isAdmin: result.isAdmin
    });

    return { created: true, existing: false, user: result };

  } catch (error) {
    console.error('✗ Error adding Keycloak user to jsreport:', error.message);
    throw error;
  }
}

// ==============================================
// Usage Examples
// ==============================================

// Example 1: Simple user creation
createKeycloakUser()
  .then(user => console.log('User created:', user._id))
  .catch(err => console.error('Failed:', err));

// Example 2: Check if exists before creating
createKeycloakUserIfNotExists('john.doe@example.com', false)
  .then(result => {
    if (result.exists) {
      console.log('User already exists with ID:', result.user._id);
    } else {
      console.log('New user created with ID:', result.user._id);
    }
  });

// Example 3: Complete with config
addKeycloakUserToJsreport({
  jsreportUrl: 'http://localhost:5488',
  adminUsername: 'admin',
  adminPassword: 'password',
  keycloakUsername: 'keycloak.user@example.com',
  isAdmin: false,
  checkExisting: true
}).then(result => {
  console.log('Result:', result);
}).catch(err => {
  console.error('Error:', err);
});

// Example 4: Bulk sync from Keycloak
const keycloakUsers = [
  { email: 'user1@example.com', roles: ['user'], enabled: true },
  { email: 'admin@example.com', roles: ['admin'], enabled: true },
  { email: 'user2@example.com', roles: ['user'], enabled: true }
];

syncKeycloakUsers(keycloakUsers)
  .then(results => {
    const successful = results.filter(r => r.success).length;
    console.log(`Synced ${successful}/${results.length} users`);
  });

// ==============================================
// IMPORTANT NOTES for Keycloak Integration:
// ==============================================
// 1. The username in jsreport MUST exactly match the Keycloak username/email
// 2. Do NOT include a password field - Keycloak handles authentication
// 3. Users will authenticate through Keycloak SSO
// 4. jsreport uses the user record for authorization (permissions, roles)
// 5. Make sure jsreport is properly configured with Keycloak in jsreport.config.json:
//    {
//      "extensions": {
//        "authentication": {
//          "cookieSession": {
//            "secret": "your-secret"
//          },
//          "admin": {
//            "username": "admin",
//            "password": "password"
//          },
//          "enabled": true
//        }
//      }
//    }
// 6. Keycloak configuration should be in place with proper client setup
*/
